import { LoadingSpinner } from '@/components/LoadingSpinner';
import { AiUnderwritingCtaAlert } from '@/shared/components/ai-underwriting-cta-alert';
import {
  AdjustedQuotesBanner,
  DEFAULT_FILTERS_STATE,
  GapFillBanner,
  QuoteErrorDisplay,
  ShopInsuranceAcknowledgmentOverlay,
  ShopInsuranceContext,
  ShopInsuranceFiltersSidebar,
  ShopInsuranceHeader,
  ShopInsuranceNotice,
  ShopInsuranceTabContent,
  ShopInsuranceTabsWrapper,
  getAssetCare20PayEligibility,
  getHideSinglePayAnd10Pay,
  getShowSinglePayAnd10Pay,
  clampPaymentOptionToEligibility,
  getShowShortTermCareOptions,
  getShowTraditionalLTCOptions,
  SHORT_TERM_CARE_PAYMENT_OPTION,
  ShortTermCareShopView,
  isClientMarriedOrPartnered,
  TRADITIONAL_LTC_PAYMENT_OPTIONS,
  useBenefitDesignInputs,
  useBenefitDesignInputsStore,
  useCarrierExclusionsActions,
  useCarrierExclusionsStore,
  useBenefitTypeFilterStore,
  useEligibilityFiltersActions,
  useEligibilityFiltersStore,
  useQuoteFetching,
  useQuoteSelection,
  useSelectedEligibilityFilters,
  useShopInsuranceAcknowledgment,
  useShopInsuranceModeQuotes,
  useShopInsuranceState,
  useShopInsuranceStoreReset,
  useShopInsuranceTraditionalLTC,
  useShopInsuranceUnderwriting,
  useSliderBounds,
  useSliderBoundsSync,
  getCachedFilterDefaults,
  toMinimumInflationPercent,
  toLegacyInflationRange,
  MAX_INFLATION_PERCENT,
  type CombinedFilterType,
  type FilterName,
  type PaymentOption,
  type ShopInsuranceMode,
  type ShopInsuranceModalPropsOptions,
} from '@/features/shop-insurance';
import {
  AddPartnerDialog,
  LinkExistingPartnerDialog,
  PartnerRequiredDialog,
} from '@/features/add-partner';
import { PoliciesComparisonModal } from '@/features/policies-comparison';
import { AdvisorCustomDisclosures } from '@/features/client-onboarding/components/advisor-custom-disclosures';
import { Button } from '@design-system';

import { Loader } from 'lucide-react';

import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useFeatureFlags } from '@/contexts/FeatureFlagsContext';
import { useDebouncedFormValue } from '@/hooks/useDebouncedFormValue';
import { useErrorToasts } from '@/hooks/useErrorToasts';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useBeforeUnloadWarning } from '@/hooks/useBeforeUnloadWarning';
import { useShopInsuranceScrollAnchor } from '@/features/shop-insurance/hooks/use-scroll-anchor';
import { FormProvider, useForm } from 'react-hook-form';

import {
  selectActiveFundingSourceOperation,
  selectAllPaymentOptionQuotes,
  selectClient,
  selectClientCalculationSettings,
  selectFeatureFlags,
  selectMaybeClientPartner,
  selectQuotes,
  selectQuotesError,
  selectQuotesIsLoading,
  selectTraditionalLTCQuotes,
  selectHasActiveIllustrationRequest,
  selectSession,
  selectSessionAdvisor,
} from '@/model';
import { isKnownLimitationError, isUnexpectedError } from '@/util/quoteTypeGuards';
import { SessionType, isTobacco, zipCodeToState } from '@shared';

interface FormData {
  premium: number;
  useCustomPremiumInput: boolean;
  benefitDuration: CombinedFilterType;
  /** Minimum inflation protection %; 0 means unfiltered. Used when the flag is on. */
  inflationProtection: number;
  /** Legacy AOB/COB range; used when `inflationProtectionMinimum` is off. */
  inflationProtectionRange: CombinedFilterType;
  inflationTypeFilter: 'all' | 'simple' | 'compound';
}

export function ShopInsuranceContent({
  onClose,
  queryOptions,
}: {
  onClose: () => void;
  queryOptions?: ShopInsuranceModalPropsOptions;
}) {
  const navigate = useNavigate();
  const [showAddPartnerDialog, setShowAddPartnerDialog] = useState(false);
  const [showLinkExistingDialog, setShowLinkExistingDialog] = useState(false);
  const [showComparePolicies, setShowComparePolicies] = useState(false);
  const { sessionType } = useSelector(selectSession);
  const isAdvisor = sessionType === SessionType.advisor || sessionType === SessionType.wizard;
  const { flags } = useFeatureFlags();
  const skipAutoFetch = queryOptions?.skipAutoFetch ?? false;
  useErrorToasts({ open: true });

  // Illustration generation takes 1-2 minutes and does not survive a reload, so warn before the
  // page goes away. Native prompt only — the app has no in-app navigation blocker to reuse.
  const hasActiveIllustrationRequest = useSelector(selectHasActiveIllustrationRequest);
  useBeforeUnloadWarning(hasActiveIllustrationRequest);

  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  useShopInsuranceScrollAnchor(scrollContainerRef);

  const quotes = useSelector(selectQuotes);
  const traditionalLTCQuotes = useSelector(selectTraditionalLTCQuotes);
  const allPaymentOptionQuotes = useSelector(selectAllPaymentOptionQuotes);
  const client = useSelector(selectClient);
  const clientId = client?.clientId;
  const advisor = useSelector(selectSessionAdvisor);
  const { advisorId } = advisor;

  useShopInsuranceStoreReset(clientId);

  const savedFilterDefaults = useMemo(() => {
    const cached = getCachedFilterDefaults(advisorId);
    if (cached) return cached;
    return (advisor?.shopInsuranceFilterDefaults ?? null) as ShopInsuranceFilterDefaults | null;
  }, [advisorId, advisor?.shopInsuranceFilterDefaults]);
  const clientPartner = useSelector(selectMaybeClientPartner);
  const clientCalculationSettings = useSelector(selectClientCalculationSettings);
  const state = zipCodeToState(client.intakeSurvey.clientZipCode);


  const selectedEligibilityFilters = useSelectedEligibilityFilters();
  const { removeEligibilityFilter, removeFeatureBenefitsFilter } = useEligibilityFiltersActions();
  const { toggleCarrier } = useCarrierExclusionsActions();

  const error = useSelector(selectQuotesError);
  const isLoading = useSelector(selectQuotesIsLoading);
  const featureFlags = useSelector(selectFeatureFlags);
  const isD2C = sessionType === SessionType.client && !!featureFlags.d2cMode;

  const isShortTermCareEligible = getShowShortTermCareOptions(
    featureFlags.quoteApplyManhattanLife,
    client.intakeSurvey.clientZipCode,
    client.intakeSurvey.clientBirthDate,
  );

  const {
    selectedTab,
    setSelectedTab,
    paymentOption,
    mode,
    expandedIndices,
    setExpandedIndices,
    hasAcknowledged,
    showAcknowledgment,
    selectedBuyerOption,
    hasPartnerWithDemographics,
    hasSameGenderPartner,
    showPartnerRequiredDialog,
    setShowPartnerRequiredDialog,
    handleTabChange,
    handleCardToggle,
    handleAcknowledgment,
    setShowAcknowledgment,
    handleBuyerOptionChange,
    handlePaymentOptionChange,
    handleModeChange,
  } = useShopInsuranceState({
    hideSinglePayAnd10Pay: getHideSinglePayAnd10Pay(
      featureFlags.hideSinglePayAnd10Pay,
      featureFlags.quoteApplyNewYorkLife,
      client.intakeSurvey.clientBirthDate,
    ),
    providedBuyerOption: queryOptions?.buyerOption,
    // Saved advisor defaults and query params travel across clients, so a
    // restored 'Short-Term Care' can land on a client who is not STC-eligible:
    // the option would be absent from the dropdown while still being the
    // selected value — a blank selector, no quotes, and no way back.
    providedPaymentOption: clampPaymentOptionToEligibility(
      queryOptions?.paymentOption ??
        (savedFilterDefaults?.paymentOption as PaymentOption | undefined),
      isShortTermCareEligible,
      getAssetCare20PayEligibility(client.intakeSurvey).isEligible,
    ),
    providedSelectedTab: queryOptions?.selectTabOption,
    providedMode:
      queryOptions?.mode ?? (savedFilterDefaults?.mode as ShopInsuranceMode | undefined),
  });

  const { fetchQuotesForPolicyType } = useQuoteFetching({
    open: true,
    selectedTab,
    paymentOption,
    hasPartnerWithDemographics,
    skipAutoFetch,
  });

  const sliderBounds = useSliderBounds(quotes, selectedTab);
  const { isQuoteSelected } = useQuoteSelection({ selectedTab });

  const returnOnInvestmentCalculationType =
    clientCalculationSettings?.returnOnInvestmentCalculationType || 'total';

  const formMethods = useForm<FormData>({
    defaultValues: {
      benefitDuration:
        (savedFilterDefaults?.benefitDuration as CombinedFilterType) ??
        DEFAULT_FILTERS_STATE.benefitDuration,
      inflationProtection:
        toMinimumInflationPercent(savedFilterDefaults?.inflationProtection) ??
        DEFAULT_FILTERS_STATE.inflationProtection,
      inflationProtectionRange:
        (toLegacyInflationRange(
          savedFilterDefaults?.inflationProtection,
          MAX_INFLATION_PERCENT,
        ) as CombinedFilterType) ?? DEFAULT_FILTERS_STATE.inflationProtectionRange,
      inflationTypeFilter:
        (savedFilterDefaults?.inflationType as 'all' | 'simple' | 'compound') ??
        DEFAULT_FILTERS_STATE.inflationTypeFilter,
      useCustomPremiumInput: savedFilterDefaults?.useCustomPremiumInput ?? false,
      premium: sliderBounds.defaultValue,
    },
  });

  const { watch, reset, getValues, setValue } = formMethods;

  // Slider-driven fields subscribe without rendering, so dragging does not re-render
  // this tree on every step — only when the value settles.
  //
  // Premium is committed once per interaction (slider release, or a fully in-range entry
  // in the custom field) rather than once per step, so it needs only enough delay to
  // coalesce a burst of keystrokes — any more is latency the user feels as slow quotes.
  const premiumValue = useDebouncedFormValue<FormData, number>(formMethods, 'premium', 100);
  const benefitDurationValue = useDebouncedFormValue<FormData, CombinedFilterType>(
    formMethods,
    'benefitDuration',
    200,
  );
  const inflationProtectionValue = useDebouncedFormValue<FormData, number>(
    formMethods,
    'inflationProtection',
    200,
  );
  const inflationProtectionRangeValue = useDebouncedFormValue<FormData, CombinedFilterType>(
    formMethods,
    'inflationProtectionRange',
    200,
  );
  const inflationTypeFilterValue = watch('inflationTypeFilter');
  const useMinimumInflation = flags.inflationProtectionMinimum ?? false;

  // Full cost the client faces = care + out-of-pocket room & board (IL only).
  // Room & board is never coverable, so IL coverage can't reach 100%.
  const totalCost = client.allPhaseCosts.allPhaseInflatedTotalCost;
  const [isGapFillMode, setIsGapFillMode] = useState(false);
  const setGapFillMode = useCallback((active: boolean) => setIsGapFillMode(active), []);
  const isCoverageGapEnabled = flags.coverageGap ?? false;

  useSliderBoundsSync({
    sliderBounds,
    paymentOption,
    reset,
    getValues,
    setValue,
    savedFilterDefaults,
  });

  const storeDefaultsAppliedForClientRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (storeDefaultsAppliedForClientRef.current === clientId || !savedFilterDefaults) return;
    storeDefaultsAppliedForClientRef.current = clientId;
    const { actions: carrierActions } = useCarrierExclusionsStore.getState();
    const { actions: benefitTypeActions } = useBenefitTypeFilterStore.getState();
    const { actions: eligibilityActions } = useEligibilityFiltersStore.getState();

    if (savedFilterDefaults.hiddenCarriers) {
      carrierActions.setHiddenCarriers(savedFilterDefaults.hiddenCarriers);
    }
    if (savedFilterDefaults.benefitPaymentType) {
      benefitTypeActions.setSelectedBenefitPaymentType(
        savedFilterDefaults.benefitPaymentType as any,
      );
    }
    if (savedFilterDefaults.eligibilityFilters) {
      eligibilityActions.setSelectedEligibilityFilters(savedFilterDefaults.eligibilityFilters);
    }
    if (savedFilterDefaults.featureBenefitsFilters) {
      eligibilityActions.setSelectedFeatureBenefitsFilters(
        savedFilterDefaults.featureBenefitsFilters,
      );
    }
    if (savedFilterDefaults.benefitDesignInputs) {
      const { actions: bdActions } = useBenefitDesignInputsStore.getState();
      const bd = savedFilterDefaults.benefitDesignInputs;
      if (bd.monthlyBenefit != null) bdActions.setMonthlyBenefit(bd.monthlyBenefit);
      if (bd.benefitDuration) bdActions.setBenefitDuration(bd.benefitDuration as any);
      // Hydrate both shapes so the saved default survives the flag being toggled either way.
      const savedMinimumInflation = toMinimumInflationPercent(bd.inflationProtection);
      if (savedMinimumInflation !== null) {
        bdActions.setInflationProtection(savedMinimumInflation);
      }
      const savedInflationRange = toLegacyInflationRange(
        bd.inflationProtection,
        MAX_INFLATION_PERCENT,
      );
      if (savedInflationRange) {
        bdActions.setInflationProtectionRange(savedInflationRange as CombinedFilterType);
      }
      if (bd.inflationTypes) bdActions.setInflationTypes(bd.inflationTypes as any);
      if (bd.benefitPaymentType) bdActions.setBenefitPaymentType(bd.benefitPaymentType as any);
    }
  }, [savedFilterDefaults, clientId]);

  const benefitDesignInputs = useBenefitDesignInputs();

  const availableCarriers = useMemo(() => {
    const carrierSet = new Set<string>();
    quotes?.forEach(q => {
      if (q.carrierName) carrierSet.add(q.carrierName);
    });
    return Array.from(carrierSet).sort();
  }, [quotes]);

  const { filteredTraditionalLTCQuotes } = useShopInsuranceTraditionalLTC({
    paymentOption,
    selectedBuyerOption,
    hasPartnerWithDemographics,
    advisorId,
    returnOnInvestmentCalculationType,
  });

  const {
    adjustedQuotes,
    isAdjusted,
    benefitDesignQuoteGroups,
    benefitDesignAvailableOptions,
    modeAdjustedQuotes,
    modeTraditionalLTCQuotes,
    selectedFeatureBenefitsFilters,
    hiddenCarriers,
  } = useShopInsuranceModeQuotes({
    mode,
    selectedTab,
    client,
    quotes,
    allPaymentOptionQuotes,
    premiumValue,
    benefitDurationValue,
    inflationProtectionValue,
    inflationProtectionRangeValue,
    inflationTypeFilterValue,
    benefitDesignInputs,
    returnOnInvestmentCalculationType,
    hasPartnerWithDemographics,
    filteredTraditionalLTCQuotes,
    isGapFillMode,
    totalCost,
  });

  useShopInsuranceAcknowledgment({
    open: true,
    isLoading,
    quotes,
    hasAcknowledged,
    setShowAcknowledgment,
  });

  const {
    eligibilityCounts,
    filteredAdjustedQuotes,
    filteredTraditionalLTCQuotesWithEligibility,
    showEligibilityFilter,
  } = useShopInsuranceUnderwriting({
    clientId: client?.clientId,
    selectedTab,
    adjustedQuotes: modeAdjustedQuotes,
    traditionalLTCQuotes: modeTraditionalLTCQuotes,
  });

  function handleTabChangeWithFetch(tabValue: string) {
    const tabChanged = handleTabChange(tabValue);
    if (tabChanged) {
      fetchQuotesForPolicyType(tabValue as ShopInsuranceTab, paymentOption);
    }
  }

  function handleOnClose() {
    localStorage.removeItem('benefitDurationNotice');
    localStorage.removeItem('inflationProtectionNotice');
    onClose();
  }

  const handleRemoveFilter = useCallback(
    (filter: FilterName) => {
      if (filter === 'inflationProtection') {
        setValue('inflationProtection', DEFAULT_FILTERS_STATE.inflationProtection);
        setValue('inflationProtectionRange', DEFAULT_FILTERS_STATE.inflationProtectionRange);
      } else if (filter === 'inflationType') {
        setValue('inflationTypeFilter', DEFAULT_FILTERS_STATE.inflationTypeFilter);
      } else if (filter === 'benefitDuration') {
        setValue('benefitDuration', DEFAULT_FILTERS_STATE.benefitDuration);
      }
    },
    [setValue],
  );

  const activeFundingSourceOperation = useSelector(selectActiveFundingSourceOperation);
  const hasSelectedPolicies =
    Object.keys(client?.multipleFundingSources?.ltcPolicy ?? {}).length > 0;
  const hasQuotesLoaded = (quotes?.length ?? 0) > 0;

  const hasAnyReturnedQuotes = hasQuotesLoaded || traditionalLTCQuotes.length > 0;
  const isReloading = activeFundingSourceOperation !== null;
  const isInitialLoading = isLoading && !hasQuotesLoaded;
  const hasUnexpectedError = isUnexpectedError(error);
  const knownLimitationError = isKnownLimitationError(error) ? error : null;
  const showContentArea = !isInitialLoading && !hasUnexpectedError;

  const isShortTermCareView = paymentOption === SHORT_TERM_CARE_PAYMENT_OPTION;

  const tabContent = useMemo(
    () => (
      <ShopInsuranceTabContent
        filteredAdjustedQuotes={filteredAdjustedQuotes}
        filteredTraditionalLTCQuotesWithEligibility={filteredTraditionalLTCQuotesWithEligibility}
        expandedIndices={expandedIndices}
        selectedTab={selectedTab}
        clientPartner={clientPartner}
        premiumValue={premiumValue}
        paymentOption={paymentOption}
        advisorId={advisorId}
        isQuoteSelected={isQuoteSelected}
        handleCardToggle={handleCardToggle}
        onClose={handleOnClose}
        knownLimitationError={knownLimitationError}
        hasSameGenderPartner={hasSameGenderPartner}
        mode={mode}
        benefitDesignQuoteGroups={benefitDesignQuoteGroups}
        stateOfResidence={state.stateAbbreviation}
      />
    ),
    [
      filteredAdjustedQuotes,
      filteredTraditionalLTCQuotesWithEligibility,
      expandedIndices,
      selectedTab,
      clientPartner,
      premiumValue,
      paymentOption,
      advisorId,
      isQuoteSelected,
      handleCardToggle,
      handleOnClose,
      knownLimitationError,
      hasSameGenderPartner,
      mode,
      benefitDesignQuoteGroups,
      state.stateAbbreviation,
    ],
  );

  // Count of quotes visible on screen after filters, matching whichever list the
  // current mode/payment option actually renders. Life and traditional LTC quotes
  // are never shown together — traditional belongs to the Annually/Monthly options.
  const isTraditionalPayment = TRADITIONAL_LTC_PAYMENT_OPTIONS.includes(paymentOption);
  const visibleQuoteCount =
    mode === 'benefit-design'
      ? benefitDesignQuoteGroups.length
      : isTraditionalPayment
        ? filteredTraditionalLTCQuotesWithEligibility.length
        : filteredAdjustedQuotes.length;

  const shopInsuranceFiltersProps = {
    showEligibilityFilter: flags.nycLifeDemo ? false : showEligibilityFilter,
    eligibilityCounts,
    mode,
    handleModeChange,
    benefitDesignAvailableOptions,
    availableCarriers,
    visibleQuoteCount,
  };

  const isMarriedOrPartnered = isClientMarriedOrPartnered(
    client?.intakeSurvey?.clientMaritalStatus,
    hasPartnerWithDemographics,
  );

  const maxCoverageQuote = useMemo(() => {
    if (!adjustedQuotes.length) return undefined;
    return adjustedQuotes.reduce((best, q) =>
      (q.projectedCostCoverage ?? 0) > (best.projectedCostCoverage ?? 0) ? q : best,
    );
  }, [adjustedQuotes]);

  return (
    <div
      className="relative flex h-full flex-col overflow-hidden"
      data-testid="shop-insurance-content"
    >
      <ShopInsuranceContext.Provider
        value={{
          sliderBounds,
          paymentOption,
          handlePaymentOptionChange,
          showTraditionalLTCOptions: getShowTraditionalLTCOptions(
            client.intakeSurvey.clientZipCode,
            client.intakeSurvey.clientBirthDate,
          ),
          showShortTermCareOption: isShortTermCareEligible,
          showSinglePayAnd10Pay: getShowSinglePayAnd10Pay(
            featureFlags.hideSinglePayAnd10Pay,
            featureFlags.quoteApplyNewYorkLife,
            client.intakeSurvey.clientBirthDate,
          ),
          show20Pay: getAssetCare20PayEligibility(client.intakeSurvey).isEligible,
          maxCoverageQuote:
            isCoverageGapEnabled && maxCoverageQuote
              ? {
                  premiumAmount: maxCoverageQuote.premiumAmount,
                  projectedCostCoverage: maxCoverageQuote.projectedCostCoverage ?? 0,
                  maxAchievableCoverage:
                    maxCoverageQuote.maxAchievableCoverage ??
                    maxCoverageQuote.projectedCostCoverage ??
                    0,
                  carrierName: maxCoverageQuote.carrierName,
                }
              : undefined,
          isGapFillMode: isCoverageGapEnabled && isGapFillMode,
          setGapFillMode: isCoverageGapEnabled ? setGapFillMode : () => {},
        }}
      >
        <FormProvider {...formMethods}>
          <ShopInsuranceAcknowledgmentOverlay
            open={showAcknowledgment && !hasAcknowledged}
            onAcknowledge={handleAcknowledgment}
          />

          {/* HEADER — sticky above scroll */}
          <div className="border-border bg-background sticky top-0 z-40 shrink-0 border-b px-4">
            <div className="mx-auto w-full max-w-7xl">
              <ShopInsuranceHeader
                isLoading={isLoading}
                error={error}
                onClose={handleOnClose}
              />
            </div>
          </div>

          {/* BODY — scrollable area */}
          <div
            ref={scrollContainerRef}
            className="relative min-h-0 flex-1 overflow-y-auto px-4 pt-6 lg:pt-0"
          >
            {isReloading && (
              <div className="bg-background/50 fixed inset-0 z-10 grid h-screen w-screen place-items-center">
                <Loader className="text-primary size-8 animate-spin" />
              </div>
            )}
            <div className="mx-auto w-full max-w-7xl">
              {isInitialLoading && (
                <div
                  className="flex min-h-[60vh] items-center justify-center"
                  data-testid="shop-insurance-loading"
                >
                  <LoadingSpinner testId="shop-insurance-loading-spinner" />
                </div>
              )}
              {hasUnexpectedError && (
                <div className="py-10">
                  <QuoteErrorDisplay
                    error={error}
                    onRetry={() => fetchQuotesForPolicyType(selectedTab, paymentOption)}
                  />
                </div>
              )}

              {showContentArea && (
                <div className="flex flex-col lg:flex-row lg:gap-x-8">
                  {/* SIDEBAR — fixed width, own scroll if content overflows */}
                  {/* Kept in the short-term care view so the payment-option
                      selector stays reachable: that view has no life quotes, so
                      hasAnyReturnedQuotes would hide the sidebar and strand the
                      advisor with no way back. */}
                  {(hasAnyReturnedQuotes || isShortTermCareView) && (
                    <ShopInsuranceFiltersSidebar {...shopInsuranceFiltersProps} />
                  )}

                  {/* RIGHT COLUMN */}
                  <div className="flex flex-1 flex-col">
                    {!isShortTermCareView && (
                      <div className="space-y-3 pt-6 empty:hidden">
                        <AiUnderwritingCtaAlert />
                      </div>
                    )}
                    {!isShortTermCareView && mode === 'premium-optimization' && (
                      <div className="bg-background sticky top-0 z-50 -mx-1 shrink-0 space-y-2 px-1 pb-2 pt-6 empty:hidden">
                        {isCoverageGapEnabled && <GapFillBanner />}
                        {(isAdjusted.length > 0 ||
                          (!flags.nycLifeDemo &&
                            showEligibilityFilter &&
                            selectedEligibilityFilters.length > 0) ||
                          selectedFeatureBenefitsFilters.length > 0 ||
                          hiddenCarriers.length > 0) && (
                          <AdjustedQuotesBanner
                            premiumValue={premiumValue}
                            benefitDurationValue={benefitDurationValue}
                            inflationProtectionValue={inflationProtectionValue}
                            inflationProtectionRangeValue={inflationProtectionRangeValue}
                            useMinimumInflation={useMinimumInflation}
                            inflationTypeFilterValue={inflationTypeFilterValue}
                            appliedFilters={isAdjusted}
                            onRemoveFilter={handleRemoveFilter}
                            selectedEligibilityFilters={
                              flags.nycLifeDemo || !showEligibilityFilter
                                ? []
                                : selectedEligibilityFilters
                            }
                            onRemoveEligibilityFilter={removeEligibilityFilter}
                            selectedFeatureBenefitsFilters={selectedFeatureBenefitsFilters}
                            onRemoveFeatureBenefitsFilter={removeFeatureBenefitsFilter}
                            hiddenCarriers={hiddenCarriers}
                            onRemoveHiddenCarrier={toggleCarrier}
                          />
                        )}
                      </div>
                    )}

                    {isShortTermCareView ? (
                      <div className="pt-6">
                        <ShortTermCareShopView
                          advisorId={advisorId}
                          stateAbbreviation={state.stateAbbreviation}
                          clientBirthDate={client.intakeSurvey.clientBirthDate}
                          tobacco={isTobacco(client.intakeSurvey)}
                        />
                      </div>
                    ) : (
                      <div className="pt-6">
                        <ShopInsuranceTabsWrapper
                          hasPartnerWithDemographics={hasPartnerWithDemographics}
                          isMarriedOrPartnered={isMarriedOrPartnered}
                          paymentOption={paymentOption}
                          selectedTab={selectedTab}
                          selectedBuyerOption={selectedBuyerOption}
                          tabContent={tabContent}
                          handleTabChangeWithFetch={handleTabChangeWithFetch}
                          handleBuyerOptionChange={handleBuyerOptionChange}
                          featureFlags={featureFlags}
                          mode={mode}
                        />
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {showContentArea && (
              <>
                <div className="bg-muted mx-auto mt-16 w-full max-w-7xl rounded-xl border p-4">
                  <ShopInsuranceNotice />
                </div>
                <AdvisorCustomDisclosures className="mx-auto mb-40 mt-2 w-full max-w-7xl" />
              </>
            )}
          </div>
        </FormProvider>
      </ShopInsuranceContext.Provider>

      {hasSelectedPolicies && (
        <div className="pointer-events-none fixed bottom-0 left-0 right-0 z-20 flex justify-center md:bottom-6">
          <div className="bg-background pointer-events-auto flex w-full flex-wrap gap-2 rounded-none border-t px-3 py-4 shadow-xl shadow-black/5 md:w-max md:flex-nowrap md:rounded-full md:border md:py-3">
            <Button
              size="lg"
              variant="outline"
              className="h-10 rounded-full px-4 text-sm md:h-11 md:px-6 md:text-base"
              onClick={() =>
                navigate(`/clients/${client.clientId}/onboarding/policy-application-dashboard`)
              }
            >
              Start Application
            </Button>
            <Button
              size="lg"
              className="h-10 rounded-full px-4 text-sm md:h-11 md:px-6 md:text-base"
              onClick={() => setShowComparePolicies(true)}
            >
              Compare Policies
            </Button>
          </div>
        </div>
      )}

      <PoliciesComparisonModal
        open={showComparePolicies}
        onOpenChange={setShowComparePolicies}
      />

      {!isD2C && (
        <>
          <PartnerRequiredDialog
            open={showPartnerRequiredDialog}
            onOpenChange={setShowPartnerRequiredDialog}
            onTakeMeToIt={() => {
              setShowPartnerRequiredDialog(false);
              setShowAddPartnerDialog(true);
            }}
            onLinkExisting={
              isAdvisor
                ? () => {
                    setShowPartnerRequiredDialog(false);
                    setShowLinkExistingDialog(true);
                  }
                : undefined
            }
          />

          {isAdvisor && (
            <LinkExistingPartnerDialog
              open={showLinkExistingDialog}
              client={client}
              onClose={() => setShowLinkExistingDialog(false)}
            />
          )}

          <AddPartnerDialog
            open={showAddPartnerDialog}
            onOpenChange={setShowAddPartnerDialog}
            clientId={client.clientId}
            onSubmitSuccess={() => {
              setShowAddPartnerDialog(false);
              setSelectedTab('joint');
              setExpandedIndices([]);
            }}
          />
        </>
      )}
    </div>
  );
}
